import React from 'react';
import { useLocale } from 'locale';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { toRelativeImagePath } from 'utils/imagePath';
import { ExposureProjectData } from './types';
import {
  CardDetails,
  CardDetailsPriceSegment,
  CardLocation,
  CardPhoto,
  CardRoot,
  CardTitle,
  CtaButton,
  DeveloperAvatar,
  InsetBorder,
  ListContent,
  ListCtaContainer,
  ListGradient,
  VerticalContent,
  VerticalCtaContainer,
  VerticalGradient,
} from './styled';

export type CardVariant = 'list' | 'grid' | 'mobile';

interface Props {
  project: ExposureProjectData;
  variant: CardVariant;
  onNavigate: () => void;
}

const ExposureProjectCard: React.FC<Props> = ({ project, variant, onNavigate }) => {
  const { t, formatMoney } = useLocale();
  const constructImageURI = useImageUrlBuilder();
  const { bedsRange, priceRange, floorRange } = project;

  const imageSrc = project.imageUrl ? constructImageURI(toRelativeImagePath(project.imageUrl)) : null;
  const avatarSrc = project.developerAvatarUrl ? constructImageURI(toRelativeImagePath(project.developerAvatarUrl)) : null;

  const location = project.neighbourhood || project.city || '';

  const bedsText = (bedsRange.min !== null && bedsRange.max !== null)
    ? bedsRange.min === bedsRange.max
      ? t('exposureProjectCard.bedsSingle', { count: bedsRange.min })
      : t('exposureProjectCard.beds', { min: bedsRange.min, max: bedsRange.max })
    : null;

  const floorsText = floorRange.max !== null
    ? t('exposureProjectCard.floors', { count: floorRange.max })
    : null;

  const priceText = priceRange.min !== null
    ? t('exposureProjectCard.priceFrom', { price: formatMoney(priceRange.min, false) })
    : null;

  const lineOneSegments = [ bedsText, floorsText ].filter((s): s is string => s !== null);
  const lineOneText = lineOneSegments.join(' • ');

  let detailsContent: React.ReactNode = null;
  if (lineOneText || priceText) {
    if (!priceText) {
      detailsContent = lineOneText;
    }
    else if (variant !== 'list' && lineOneSegments.length === 2) {
      // grid/mobile: two line-one items fill the row — break price onto its own line
      detailsContent = <>{lineOneText}<br/><CardDetailsPriceSegment>{priceText}</CardDetailsPriceSegment></>;
    }
    else {
      detailsContent = <>{lineOneText ? `${lineOneText} • ` : ''}<CardDetailsPriceSegment>{priceText}</CardDetailsPriceSegment></>;
    }
  }

  const ctaLabel = t('exposureProjectCard.cta');

  if (variant === 'list') {
    return (
      <CardRoot
        variant="list"
        onClick={onNavigate}
        data-auto="search_results_exposure_project_card"
      >
        {imageSrc ? <CardPhoto variant="list" src={imageSrc} alt={project.projectName} /> : null}
        <ListGradient />
        {avatarSrc ? <DeveloperAvatar variant="list" src={avatarSrc} alt="" /> : null}
        <ListContent>
          <CardTitle variant="list">{project.projectName}</CardTitle>
          <CardLocation>{location}</CardLocation>
          {detailsContent ? <CardDetails>{detailsContent}</CardDetails> : null}
        </ListContent>
        <ListCtaContainer>
          <CtaButton
            type="button"
            data-auto="search_results_exposure_project_card_cta"
          >
            {ctaLabel}
          </CtaButton>
        </ListCtaContainer>
        <InsetBorder />
      </CardRoot>
    );
  }

  return (
    <CardRoot
      variant={variant}
      onClick={onNavigate}
      data-auto="search_results_exposure_project_card"
    >
      {imageSrc ? <CardPhoto variant={variant} src={imageSrc} alt={project.projectName} /> : null}
      <VerticalGradient variant={variant} />
      {avatarSrc ? <DeveloperAvatar variant={variant} src={avatarSrc} alt="" /> : null}
      <VerticalContent>
        <CardTitle variant={variant}>{project.projectName}</CardTitle>
        <CardLocation>{location}</CardLocation>
        {detailsContent ? <CardDetails>{detailsContent}</CardDetails> : null}
      </VerticalContent>
      <VerticalCtaContainer>
        <CtaButton
          type="button"
          data-auto="search_results_exposure_project_card_cta"
        >
          {ctaLabel}
        </CtaButton>
      </VerticalCtaContainer>
      <InsetBorder />
    </CardRoot>
  );
};

export default ExposureProjectCard;
